import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { bookableResourcesActions } from './slice';

// Backend mount is /v5/:orgID/bookableResources (camelCase) — SuperFetch prepends /v5/{orgID}.
const BASE = '/bookableResources';

// 🔥 Load all bookable resources for the org
export function* getResources() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(BASE));
		if (res.statusCode === 200) {
			yield put(bookableResourcesActions.loadResourcesSuccess({ orgID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load bookable resources.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.loadResourcesError(error));
	}
}

// 🔥 Load ONE resource — the full record, including accessChannels (and their configJson),
// which the list endpoint does not return.
export function* getResource({ payload }) {
	try {
		const res = yield call(() => SuperFetch.get(`${BASE}/${payload.resourceID}`));
		if (res.statusCode === 200) {
			yield put(bookableResourcesActions.loadResourceSuccess(res.data));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load resource.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.loadResourceError(error));
	}
}

// 🔥 Create
export function* createResource({ payload }) {
	try {
		const res = yield call(() => SuperFetch.post(BASE, payload));
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', 'Bookable resource created.');
			yield put(bookableResourcesActions.createResourceSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to create resource.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.createResourceError(error));
		Notification('error', error.message || 'Failed to create resource.');
	}
}

// 🔥 Update
export function* updateResource({ payload }) {
	try {
		const { resourceID, ...data } = payload;
		const res = yield call(() => SuperFetch.patch(`${BASE}/${resourceID}`, data));
		if (res.statusCode === 200) {
			Notification('success', 'Bookable resource updated.');
			yield put(bookableResourcesActions.updateResourceSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to update resource.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.updateResourceError(error));
		Notification('error', error.message || 'Failed to update resource.');
	}
}

// 🔥 Delete (soft) — supports ?cascade=true when there are active future bookings
export function* deleteResource({ payload }) {
	try {
		const { resourceID, cascade } = payload;
		const res = yield call(() => SuperFetch.delete(`${BASE}/${resourceID}`, {}, cascade ? { cascade: true } : {}));
		if (res.statusCode === 200) {
			Notification('success', 'Bookable resource deleted.');
			yield put(bookableResourcesActions.deleteResourceSuccess());
		} else if (res.statusCode === 409) {
			Notification('warning', res.message || 'Resource has active future bookings.');
			throw Error('blocked');
		} else {
			throw Error(res.error?.message || res.error || 'Failed to delete resource.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.deleteResourceError(error));
		if (error.message !== 'blocked') Notification('error', error.message || 'Failed to delete resource.');
	}
}

// 🔥 After any mutation, reload the list
export function* reloadAfterMutation() {
	yield put(bookableResourcesActions.loadResources());
}

// 🔥 Availability for a resource over a window
export function* getAvailability({ payload }) {
	try {
		const { resourceID, startTime, endTime } = payload;
		const res = yield call(() => SuperFetch.get(`${BASE}/${resourceID}/availability`, { startTime, endTime }));
		if (res.statusCode === 200) {
			yield put(bookableResourcesActions.loadAvailabilitySuccess(res.data));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load availability.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.loadAvailabilityError(error));
	}
}

// 🔥 Email a resource's guest booking link
export function* shareResourceEmail({ payload }) {
	try {
		const { resourceID, to, url, message } = payload;
		const res = yield call(() => SuperFetch.post(`${BASE}/${resourceID}/share-email`, { to, url, message }));
		if (res.statusCode === 200) {
			Notification('success', 'Share email sent.');
			yield put(bookableResourcesActions.shareResourceEmailSuccess());
		} else {
			throw Error(res.error?.message || res.error || 'Failed to send email.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.shareResourceEmailError(error));
		Notification('error', error.message || 'Failed to send email.');
	}
}

// 🔥 Blackout / maintenance windows
export function* loadBlackouts({ payload }) {
	try {
		const { resourceID } = payload;
		const res = yield call(() => SuperFetch.get(`${BASE}/${resourceID}/blackouts`));
		if (res.statusCode === 200) {
			yield put(bookableResourcesActions.loadBlackoutsSuccess({ resourceID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load blackouts.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.loadBlackoutsError(error));
	}
}

export function* createBlackout({ payload }) {
	try {
		const { resourceID, ...body } = payload;
		const res = yield call(() => SuperFetch.post(`${BASE}/${resourceID}/blackouts`, body));
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', 'Blackout created.');
			yield put(bookableResourcesActions.createBlackoutSuccess());
			yield put(bookableResourcesActions.loadBlackouts({ resourceID }));
		} else if (res.statusCode === 409) {
			// Overlaps active future bookings — surface the conflicts so the UI can confirm an override.
			yield put(bookableResourcesActions.createBlackoutBlocked(res.data?.conflicts || []));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to create blackout.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.createBlackoutError(error));
		Notification('error', error.message || 'Failed to create blackout.');
	}
}

export function* deleteBlackout({ payload }) {
	try {
		const { resourceID, blackoutID } = payload;
		const res = yield call(() => SuperFetch.delete(`${BASE}/${resourceID}/blackouts/${blackoutID}`));
		if (res.statusCode === 200) {
			Notification('success', 'Blackout removed.');
			yield put(bookableResourcesActions.deleteBlackoutSuccess());
			yield put(bookableResourcesActions.loadBlackouts({ resourceID }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to remove blackout.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.deleteBlackoutError(error));
		Notification('error', error.message || 'Failed to remove blackout.');
	}
}

// 🔥 Custom intake questions
export function* loadQuestions({ payload }) {
	try {
		const { resourceID } = payload;
		const res = yield call(() => SuperFetch.get(`${BASE}/${resourceID}/questions`));
		if (res.statusCode === 200) {
			yield put(bookableResourcesActions.loadQuestionsSuccess({ resourceID, data: res.data }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to load questions.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.loadQuestionsError(error));
	}
}

export function* createQuestion({ payload }) {
	try {
		const { resourceID, ...body } = payload;
		const res = yield call(() => SuperFetch.post(`${BASE}/${resourceID}/questions`, body));
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', 'Question added.');
			yield put(bookableResourcesActions.createQuestionSuccess());
			yield put(bookableResourcesActions.loadQuestions({ resourceID }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to add question.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.createQuestionError(error));
		Notification('error', error.message || 'Failed to add question.');
	}
}

export function* updateQuestion({ payload }) {
	try {
		const { resourceID, questionID, ...body } = payload;
		const res = yield call(() => SuperFetch.patch(`${BASE}/${resourceID}/questions/${questionID}`, body));
		if (res.statusCode === 200) {
			yield put(bookableResourcesActions.updateQuestionSuccess());
			yield put(bookableResourcesActions.loadQuestions({ resourceID }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to update question.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.updateQuestionError(error));
		Notification('error', error.message || 'Failed to update question.');
	}
}

export function* deleteQuestion({ payload }) {
	try {
		const { resourceID, questionID } = payload;
		const res = yield call(() => SuperFetch.delete(`${BASE}/${resourceID}/questions/${questionID}`));
		if (res.statusCode === 200) {
			Notification('success', 'Question removed.');
			yield put(bookableResourcesActions.deleteQuestionSuccess());
			yield put(bookableResourcesActions.loadQuestions({ resourceID }));
		} else {
			throw Error(res.error?.message || res.error || 'Failed to remove question.');
		}
	} catch (error) {
		yield put(bookableResourcesActions.deleteQuestionError(error));
		Notification('error', error.message || 'Failed to remove question.');
	}
}

export default function* bookableResourcesSaga() {
	yield takeLatest(bookableResourcesActions.loadResources, getResources);
	yield takeLatest(bookableResourcesActions.loadResource, getResource);
	yield takeLatest(bookableResourcesActions.createResource, createResource);
	yield takeLatest(bookableResourcesActions.updateResource, updateResource);
	yield takeLatest(bookableResourcesActions.deleteResource, deleteResource);
	yield takeLatest(bookableResourcesActions.createResourceSuccess, reloadAfterMutation);
	yield takeLatest(bookableResourcesActions.updateResourceSuccess, reloadAfterMutation);
	yield takeLatest(bookableResourcesActions.deleteResourceSuccess, reloadAfterMutation);
	yield takeLatest(bookableResourcesActions.loadAvailability, getAvailability);
	yield takeLatest(bookableResourcesActions.shareResourceEmail, shareResourceEmail);
	yield takeLatest(bookableResourcesActions.loadBlackouts, loadBlackouts);
	yield takeLatest(bookableResourcesActions.createBlackout, createBlackout);
	yield takeLatest(bookableResourcesActions.deleteBlackout, deleteBlackout);
	yield takeLatest(bookableResourcesActions.loadQuestions, loadQuestions);
	yield takeLatest(bookableResourcesActions.createQuestion, createQuestion);
	yield takeLatest(bookableResourcesActions.updateQuestion, updateQuestion);
	yield takeLatest(bookableResourcesActions.deleteQuestion, deleteQuestion);
}
